// Page-Locked Articles helpers. BigInt page ids arrive as strings (graphql-ruby BigInt), so every page comparison
// goes through sameFacebookPageId.

export interface FacebookPageLike {
  facebookPageId: unknown;
  facebookPageDisplayName: string;
}

export const sameFacebookPageId = (a: unknown, b: unknown): boolean =>
  a !== null && a !== undefined && b !== null && b !== undefined && String(a) === String(b);

export const findFacebookPage = <T extends FacebookPageLike>(pages: T[], facebookPageId: unknown): T | null =>
  pages.find((page) => sameFacebookPageId(page.facebookPageId, facebookPageId)) ?? null;

export const facebookPageOptionLabel = (page: FacebookPageLike, isDefault: boolean): string =>
  `${page.facebookPageDisplayName} - ${page.facebookPageId}${isDefault ? ' (Default)' : ''}`;

// Post ids are PAGEID_POSTID. Ad ids have no prefix and return null; the backend checks those.
export const pageIdFromPostId = (postId: string): string | null => /^(\d+)_\d+$/.exec(postId.trim())?.[1] ?? null;

// Insights rows carry page fields only while the column is visible, so "Search Articles" is unchanged otherwise.
export const facebookPageRowFields = (
  article: { facebookPageId: unknown; facebookPage: { facebookPageDisplayName: string } | null },
  isVisible: boolean
) =>
  isVisible
    ? {
        facebookPageId: article.facebookPageId ? String(article.facebookPageId) : null,
        facebookPageName: article.facebookPage?.facebookPageDisplayName ?? ''
      }
    : {};

const safeDecode = (value: string) => {
  try {
    return decodeURIComponent(value);
  } catch {
    return value;
  }
};

const queryKeys = (query: string) =>
  query
    .split('#')[0]
    .split('&')
    .map((pair) => safeDecode(pair.split('=')[0] ?? '').trim())
    .filter(Boolean);

// The first query key of the URL or the additional parameters that is set per Facebook Page, if any.
export const findReservedQueryKey = (
  url: string | null | undefined,
  additionalParameters: string | null | undefined,
  reservedKeys: string[]
): string | null => {
  const urlQuery = url && url.includes('?') ? url.slice(url.indexOf('?') + 1) : '';
  return (
    [...queryKeys(urlQuery), ...queryKeys(additionalParameters ?? '')].find((key) =>
      reservedKeys.includes(key.toLowerCase())
    ) ?? null
  );
};
